package pt.at.oss.kotlin.manual.validator.common

import pt.at.oss.kotlin.core.enumeration.Country
import pt.at.oss.kotlin.core.enumeration.EU_COUNTRIES
import pt.at.oss.kotlin.core.enumeration.MS_A2
import pt.at.oss.kotlin.core.enumeration.MS_A2_EXCEPT_PT
import pt.at.oss.kotlin.core.enumeration.MemberState
import pt.at.oss.kotlin.core.enumeration.NON_EU_COUNTRIES
import pt.at.oss.kotlin.core.enumeration.PeculiarCountry
import pt.at.oss.kotlin.core.enumeration.RegistrationType
import pt.at.oss.kotlin.core.enumeration.WithCodeA2
import pt.at.oss.kotlin.manual.enumeration.CustomErrorCodes
import pt.at.oss.kotlin.manual.storage.common.VatId
import pt.lightweightform.kotlin.type.validator.ValidationError

/**
 * Created by Pedro Alfaiate on 11/05/2020.
 */

enum class VatIdType { VAT, TAX }

val FIXED_ESTAB_VAT_ID_VALIDATOR = VatIdValidator(true, arrayOf(VatIdType.VAT, VatIdType.TAX), MS_A2_EXCEPT_PT, MS_A2_EXCEPT_PT, RegistrationType.UNION, listOf(RegistrationType.UNION))
val IDS_IN_OTHER_MSS_VALIDATOR = VatIdValidator(true, arrayOf(VatIdType.VAT), MS_A2_EXCEPT_PT, emptyList(), RegistrationType.UNION, listOf(RegistrationType.UNION))
val PREV_OR_CURR_OSS_VAT_IDS_VALIDATOR = VatIdValidator(true, arrayOf(VatIdType.VAT), MS_A2, emptyList(), null, listOf(RegistrationType.UNION, RegistrationType.NON_UNION, RegistrationType.IMPORT))
val PREV_OR_CURR_OSS_INT_IDS_VALIDATOR = VatIdValidator(true, arrayOf(VatIdType.VAT), MS_A2, emptyList(), RegistrationType.INTERMEDIARY, listOf(RegistrationType.INTERMEDIARY))
val IDENTIFICATION_OF_BUSINESS_VAT_ID_VALIDATOR = VatIdValidator(true, arrayOf(VatIdType.VAT, VatIdType.TAX), EU_COUNTRIES, NON_EU_COUNTRIES + PeculiarCountry.values(), RegistrationType.UNION, listOf(RegistrationType.UNION))
// Note: We only use this for validating Non-Union NETP's nationalTaxNumber
val NON_UNION_NAT_TAX_VALIDATOR = VatIdValidator(true, arrayOf(), listOf(), listOf(), null, listOf())

class VatIdValidator(
    val isRequired: Boolean,
    val types: Array<VatIdType>,
    val vatAllowedCountries: List<WithCodeA2>,
    val taxAllowedCountries: List<WithCodeA2>,
    val initialScheme: RegistrationType?,
    val allowedSchemes: List<RegistrationType>
) {
    val allAllowedCountries = emptySet<Country>() + vatAllowedCountries + taxAllowedCountries
    val allowedCountryValues = allAllowedCountries.map { it.codeA2 }
    val allowedCountryValuesArray = allowedCountryValues.toTypedArray()
    val allowedSchemeValues = allowedSchemes.map { it.name }.toTypedArray()
    val initialSchemeValue = initialScheme?.name

    val validateVatIdSequencial = fun(value: VatId): List<ValidationError> {
        val errors = mutableListOf<ValidationError>()
        // Mutex VAT ID number vs National Tax number
        errors.addAll(vatIdIsValid(value.issuingCountry, value.vatIdNum, value.taxRefNum))
        // Scheme
        validateRequiredStrIf(value.scheme, value.vatIdNum.isNotBlank())?.also { errors.add(it) }
        validateScheme(value.scheme)?.also { errors.add(it) }
        // Issuing country
        validateRequiredStrIf(value.issuingCountry, isRequired)?.also { errors.add(it) }
        value.issuingCountry?.let { country ->
            validateAllowedValues(country, allowedCountryValues)
        }?.also {
            errors.add(it)
        }
        // VAT ID Number
        vatIdNumIsValid(value.vatIdNum)?.also { errors.add(it) }
        validateVatIdIsAllowed(value.vatIdNum, value.issuingCountry)
            ?.also { errors.add(it) }
        validateVatIdNumFormat(value.vatIdNum, value.scheme, value.issuingCountry)
            ?.also { errors.add(it) }
        // National Tax Number
        taxRefNumIsValid(value.taxRefNum)?.also { errors.add(it) }
        validateTaxRefIsAllowed(value.taxRefNum, value.issuingCountry)
            ?.also { errors.add(it) }
        validateTaxRefFormat(value.taxRefNum)?.also { errors.add(it) }

        return errors
    }

    val vatIdNumIsValid = fun(value: String): ValidationError? {
        // only fill if allowed
        return if (!value.isBlank() && !types.contains(VatIdType.VAT)) ValidationError(code = CustomErrorCodes.VAT_ID_VAT_TYPE_NOT_ALLOWED.code)
        else null
    }
    val validateVatIdIsAllowed = fun(vatId: String, issuingCountry: String?): ValidationError? =
        validateAllowedIf(vatId, vatAllowedCountries.any { it.codeA2 == issuingCountry })

    val taxRefNumIsValid = fun(value: String): ValidationError? {
        // only fill if allowed
        return if (!value.isBlank() && !types.contains(VatIdType.TAX)) ValidationError(code = CustomErrorCodes.VAT_ID_TAX_TYPE_NOT_ALLOWED.code)
        else null
    }
    val validateTaxRefIsAllowed = fun(taxRef: String, issuingCountry: String?): ValidationError? =
        validateAllowedIf(taxRef, taxAllowedCountries.any { it.codeA2 == issuingCountry })

    val vatIdIsValid = fun(issuingCountry: String?, vatIdNum: String, taxRefNum: String): Array<ValidationError> {
        val errors = mutableListOf<ValidationError>()
        // only one allowed
        if (!vatIdNum.isBlank() && !taxRefNum.isBlank()) {
            errors.add(ValidationError(code = CustomErrorCodes.VAT_ID_ONLY_ONE_ALLOWED.code))
        }
        // if isRequired, at least one required
        if (isRequired && vatIdNum.isBlank() && taxRefNum.isBlank()) {
            errors.add(ValidationError(code = CustomErrorCodes.VAT_ID_ONE_TYPE_REQUIRED.code))
        }
        // cannot be partially filled
        val countryFilled = !issuingCountry.isNullOrBlank()
        val idFilled = vatIdNum.isNotBlank() || taxRefNum.isNotBlank()
        if ((countryFilled && !idFilled) || (!countryFilled && idFilled)) {
            errors.add(ValidationError(code = CustomErrorCodes.VAT_ID_PARTIALLY_FILLED.code))
        }

        return errors.toTypedArray()
    }

    val validateScheme = fun(schemeVal: String?): ValidationError? {
        return if (!schemeVal.isNullOrBlank() && !allowedSchemeValues.contains(schemeVal)) ValidationError(code = CustomErrorCodes.VAT_ID_SCHEME_NOT_ALLOWED.code)
        else null
    }

    val validateVatIdNumFormat = fun(vatIdNum: String, schemeVal: String?, issuingCountry: String?): ValidationError? {
        val scheme = allowedSchemes.find { it.name == schemeVal }
        val country = MemberState.findByA2(issuingCountry ?: "")
        if (scheme == null || country == null || vatIdNum.isNullOrBlank()) return null
        val valid = when (scheme) {
            RegistrationType.UNION -> validMossNum(vatIdNum)
            RegistrationType.NON_UNION -> validVoesNum(vatIdNum, country)
            RegistrationType.IMPORT -> validIossNum(vatIdNum, country)
            RegistrationType.INTERMEDIARY -> validIntNum(vatIdNum, country)
        }
        return if (!valid) {
            when (scheme) {
                RegistrationType.UNION -> ValidationError(code = CustomErrorCodes.VAT_ID_INVALID_VAT_FORMAT.code)
                RegistrationType.NON_UNION -> ValidationError(code = CustomErrorCodes.VAT_ID_INVALID_VOES_FORMAT.code, data = mapOf("n3Code" to country.n3Code))
                RegistrationType.IMPORT -> ValidationError(code = CustomErrorCodes.VAT_ID_INVALID_IOSS_FORMAT.code, data = mapOf("n3Code" to country.n3Code))
                RegistrationType.INTERMEDIARY -> ValidationError(code = CustomErrorCodes.VAT_ID_INVALID_INT_FORMAT.code, data = mapOf("n3Code" to country.n3Code))
            }
        } else null
    }

    val validateTaxRefFormat = fun(taxRefNum: String): ValidationError? {
        val valid = validTaxRefNum(taxRefNum) || taxRefNum.isNullOrBlank()

        return if (!valid)
            ValidationError(code = CustomErrorCodes.VAT_ID_TAX_REF_NUM_INVALID_FORMAT.code)
        else null
    }
}
